import { useEffect } from 'react';
import { X } from 'lucide-react';
import { Paper, TagSummary } from '../types/ingredient';
import './PaperModal.css';

interface PaperModalProps {
  paper: Paper;
  tagSummary: TagSummary | undefined;
  onClose: () => void;
}

export const PaperModal = ({ paper, tagSummary, onClose }: PaperModalProps) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle click outside modal to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Determine safety badge
  const getSafetyBadge = (scale: number | undefined) => {
    if (scale === undefined) return null;
    if (scale >= 7) return { label: 'Safe', className: 'badge-safe' };
    if (scale >= 4) return { label: 'Conditionally Safe', className: 'badge-conditional' };
    return { label: 'Unsafe', className: 'badge-unsafe' };
  };

  const safetyBadge = getSafetyBadge(tagSummary?.safetyScale);

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container">
        <div className="modal-content">
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </button>
          
          <div className="modal-header">
            {safetyBadge && (
              <span className={`safety-badge ${safetyBadge.className}`}>
                {safetyBadge.label}
              </span>
            )}
            <h2>{paper.title}</h2>
          </div>

          <div className="modal-body">
            {paper.authors && paper.authors.length > 0 && (
              <div className="modal-section">
                <h3>Authors</h3>
                <p>{paper.authors.join(', ')}</p>
              </div>
            )}

            {paper.studyType && paper.studyType.length > 0 && (
              <div className="modal-section">
                <h3>Study Type</h3>
                <div className="study-type-tags">
                  {paper.studyType.map((type, index) => (
                    <span key={index} className="study-type-tag">{type}</span>
                  ))}
                </div>
              </div>
            )}

            {tagSummary && (
              <div className="modal-section summary-section">
                <h3>Summary</h3>
                
                {tagSummary.summaryDisplay && (
                  <div 
                    className="summary-content"
                    dangerouslySetInnerHTML={{ __html: tagSummary.summaryDisplay }}
                  />
                )}
                
                {tagSummary.safetyScale !== undefined && (
                  <div className="safety-scale">
                    <strong>Safety Scale:</strong> {tagSummary.safetyScale}/10
                  </div>
                )}
              </div>
            )}

            <div className="modal-section">
              <a 
                href={paper.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="view-full-paper-btn"
              >
                View Full Paper ↗
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
